import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ContactChooser, WaIcon } from "@/components/site/ContactChooser";

export default function Contact() {
  const { t, tx, lang, siteInfo } = useApp();
  useSeo("contact");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally { setLoading(false); }
  };

  const field = "w-full bg-transparent border-b border-slate-300 py-3 text-lg focus:outline-none focus:border-accent2 transition-colors placeholder:text-slate-400";

  return (
    <div className="pt-32 md:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 md:gap-20">
        <div>
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-widest text-accent2">{t.nav.contact}</span>
            <h1 className="mt-3 font-display font-black text-5xl md:text-7xl tracking-tighter">{t.contact.title}</h1>
            <p className="mt-6 max-w-md text-lg text-slate-600">{t.contact.sub}</p>
          </Reveal>

          <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
            <ContactChooser type="wa">
              <button data-testid="contact-whatsapp"
                className="inline-flex items-center gap-2 bg-wa text-white font-semibold px-6 py-3.5 hover:brightness-95 transition">
                <WaIcon /> {t.contact.whatsapp}
              </button>
            </ContactChooser>
            <ContactChooser type="tel">
              <button data-testid="contact-call"
                className="inline-flex items-center gap-2 border border-brand text-brand font-semibold px-6 py-3.5 hover:bg-brand hover:text-white transition">
                <Phone size={18} /> {t.contact.call}
              </button>
            </ContactChooser>
          </Reveal>

          <Reveal delay={0.15} className="mt-12 space-y-5 text-slate-700">
            <div className="flex gap-3"><MapPin className="text-accent2 shrink-0" size={20} /><span>{tx(siteInfo.address)}</span></div>
            {(siteInfo.contacts || []).map((c) => (
              <div key={c.phone} className="flex gap-3" data-testid={`contact-person-${(c.phone||'').replace(/[^0-9]/g,'')}`}>
                <Phone className="text-accent2 shrink-0" size={20} />
                <div>
                  <span className="block font-semibold">{c.name}</span>
                  <a className="text-slate-500 hover:text-accent2" href={`tel:${(c.phone||'').replace(/\s/g,'')}`}>{c.phone}</a>
                </div>
              </div>
            ))}
            <div className="flex gap-3"><Mail className="text-accent2 shrink-0" size={20} /><a className="hover:text-accent2" href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a></div>
            <div className="flex gap-3"><Clock className="text-accent2 shrink-0" size={20} /><div><span className="block text-xs uppercase tracking-widest text-slate-400 mb-1">{t.contact.hours}</span><span className="whitespace-pre-line">{tx(siteInfo.hours)}</span></div></div>
          </Reveal>

          <Reveal delay={0.2} className="mt-10">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">{t.contact.findus}</div>
            <div className="aspect-[16/9] border border-slate-200 overflow-hidden">
              <iframe title="map" width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(siteInfo.maps_query || tx(siteInfo.address) || "Ljubljana")}&z=13&output=embed`} />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="border border-slate-200 p-8 md:p-10 space-y-7">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">{t.contact.name}</label>
              <input data-testid="contact-name" required className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-7">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">{t.contact.email}</label>
                <input data-testid="contact-email" type="email" required className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">{t.contact.phone}</label>
                <input data-testid="contact-phone" className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">{t.contact.message}</label>
              <textarea data-testid="contact-message" required rows={5} className={field + " resize-none"} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <button data-testid="contact-submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold px-7 py-4 hover:bg-accent2 transition-colors disabled:opacity-60">
              {loading ? t.contact.sending : t.contact.send} <ArrowRight size={18} />
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
